import { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
} from 'recharts';
import type { VehicleResult, FleetTCOResponse } from '../../types';

// Phase 24.3: Year-by-year cost walk.
// Phase 25.2: bars collapsed from stacked-by-category to one *total* bar
// per year per series. The category breakdown moved into the expandable
// table below the chart, which is the only place where flat operating
// costs would tell a misleading "all bars are the same height" story.

export interface YearBreakdown {
  year: number;            // 1-indexed
  fuel: number;
  idleFuel: number;
  maintenance: number;
  insurance: number;
  registration: number;
  labor: number;           // 0 in personal mode
  depreciation: number;
}

// Category palette used by the breakdown table only (the bars themselves are
// solid totals colored by scenario, not by category). Kept here so the
// table swatches and any future per-category overlays stay aligned.
const CATEGORY_COLORS: Record<CategoryKey, string> = {
  depreciation: '#ef4444',
  fuel:         '#3a6fd4',
  idleFuel:     '#7aa3e8',
  maintenance:  '#10b981',
  insurance:    '#6d28d9',
  registration: '#fbbf24',
  labor:        '#f97316',
};

// Phase 25.3: cumulative line was previously '#e5e7eb' (light grey), which
// disappeared against both the bars and the sensitivity-band fill. Single-
// scenario charts now use a dark teal that has high contrast against the
// blue bar color (#3a6fd4) and against white space, but does not collide
// with either scenario identity color used in the compare variant.
const CUMULATIVE_COLOR = '#0F766E';
const BAND_COLOR = '#94a3b8';

// Single-scenario chart uses one neutral bar color across all years.
const SINGLE_BAR_COLOR = '#3a6fd4';

// Phase 25.2: shared sensitivity label so both chart variants stay in sync.
const SENSITIVITY_LABEL = '±20% fuel & ±10% residual (simultaneous worst/best case)';
// Threshold below which a sensitivity band is judged "narrow" relative to
// total TCO and a low-sensitivity callout is shown. Picked at 10% as a
// starting point; QA against the demo scenarios suggests the Accord vs BMW
// pair has a band ~5–6% wide, which should trigger the callout.
const LOW_SENSITIVITY_THRESHOLD_PCT = 10;

interface Props {
  years: YearBreakdown[];
  // Sensitivity adjustment inputs. The component derives low/high envelopes
  // by re-multiplying fuel and depreciation lines per the rule documented in
  // the toggle label. These are the *baseline horizon totals* used to scale.
  totalFuel: number;          // baseline lifetime fuel (used as ±20% pivot)
  totalDepreciation: number;  // baseline lifetime depreciation (used as ±10% residual pivot)
  // Title context — fleet-mode adds "Fleet total — all N vehicles" label.
  fleetVehicleCount?: number;
}

function fmtDollar(v: number): string {
  if (Math.abs(v) < 100) {
    return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  return '$' + Math.round(v).toLocaleString('en-US');
}

function yearTotal(y: YearBreakdown): number {
  return y.fuel + y.idleFuel + y.maintenance + y.insurance + y.registration + y.labor + y.depreciation;
}

type CategoryKey = 'depreciation' | 'fuel' | 'idleFuel' | 'maintenance' | 'insurance' | 'registration' | 'labor';

const CATEGORY_ROWS: Array<{ key: CategoryKey; label: string }> = [
  { key: 'depreciation', label: 'Depreciation' },
  { key: 'fuel',         label: 'Fuel' },
  { key: 'idleFuel',     label: 'Idle Fuel' },
  { key: 'maintenance',  label: 'Maintenance' },
  { key: 'insurance',    label: 'Insurance' },
  { key: 'registration', label: 'Registration' },
  { key: 'labor',        label: 'Labor' },
];

// ── Category breakdown table ──────────────────────────────────────────────────
//
// Phase 25.2: collapsible per-year detail that lives under the chart. Shows a
// $ value per cell with a YoY indicator (↑ red %, ↓ green %, — grey for
// flat). Categories with all-zero rows are hidden so personal-mode tables
// drop Idle Fuel and Labor automatically.

function CategoryTableBody({ years }: { years: YearBreakdown[] }) {
  if (years.length === 0) return null;
  const enabled = CATEGORY_ROWS.filter((r) => years.some((y) => y[r.key] > 0));

  const cellPad = '6px 10px';
  const headerStyle: React.CSSProperties = {
    fontSize: 11,
    color: 'var(--tco-ink-3)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    padding: cellPad,
    textAlign: 'right',
    fontWeight: 600,
  };
  const valueCell: React.CSSProperties = {
    padding: cellPad,
    fontSize: 12,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    color: 'var(--tco-ink)',
    whiteSpace: 'nowrap',
  };
  // Phase 25.3: the label column was collapsing under wide tables, letting
  // category names ("Depreciation", "Maintenance") wrap into the year-1
  // value cell and overlap. Pin a min-width and prevent wrapping; the
  // dollar cells already nowrap so the table grows horizontally rather
  // than stacking text on top of itself.
  const labelCell: React.CSSProperties = {
    padding: cellPad,
    fontSize: 12,
    textAlign: 'left',
    color: 'var(--tco-ink-2)',
    whiteSpace: 'nowrap',
    minWidth: 140,
  };

  return (
    <div style={{ overflowX: 'auto' }}>
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: 6,
      }}
    >
      <thead>
        <tr style={{ background: 'var(--tco-line-soft)' }}>
          <th style={{ ...headerStyle, textAlign: 'left' }}>Category</th>
          {years.map((y) => (
            <th key={y.year} style={headerStyle}>Yr {y.year}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {enabled.map((row, ri) => (
          <tr key={row.key} style={{ background: ri % 2 === 0 ? 'transparent' : 'var(--tco-line-soft)' }}>
            <td style={labelCell}>
              <span
                aria-hidden
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: CATEGORY_COLORS[row.key],
                  marginRight: 6,
                  verticalAlign: 'middle',
                }}
              />
              {row.label}
            </td>
            {years.map((y, i) => {
              const v = y[row.key];
              if (i === 0) {
                return (
                  <td key={y.year} style={valueCell}>
                    {fmtDollar(v)}
                  </td>
                );
              }
              const prev = years[i - 1][row.key];
              const diff = v - prev;
              const pct = prev > 0 ? (diff / prev) * 100 : 0;
              // Flat = within $1 (handles rounding fuzz on flat operating costs).
              if (Math.abs(diff) < 1) {
                return (
                  <td key={y.year} style={valueCell}>
                    <span>{fmtDollar(v)}</span>
                    <span style={{ color: 'var(--tco-ink-3)', marginLeft: 6 }}>—</span>
                  </td>
                );
              }
              const up = diff > 0;
              const arrowColor = up ? 'var(--tco-neg)' : 'var(--tco-accent)';
              const arrow = up ? '↑' : '↓';
              return (
                <td key={y.year} style={valueCell}>
                  <span>{fmtDollar(v)}</span>
                  <span style={{ color: arrowColor, marginLeft: 6 }}>
                    {arrow} {Math.abs(pct).toFixed(0)}%
                  </span>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function CategoryDetailDisclosure({
  children,
  testId,
}: {
  children: React.ReactNode;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const arrow = open ? '▼' : '▶';
  return (
    <div
      data-testid={testId}
      style={{
        marginTop: 12,
        padding: '8px 0 0',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          color: 'var(--tco-ink-2)',
          fontSize: 11,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {arrow} {open ? 'Hide' : 'Show'} category detail
      </button>
      {open && <div style={{ marginTop: 8 }}>{children}</div>}
    </div>
  );
}

// ── Single-scenario chart ─────────────────────────────────────────────────────

export default function YearByCostChart({
  years,
  totalFuel,
  totalDepreciation,
  fleetVehicleCount,
}: Props) {
  const [showCumulative, setShowCumulative] = useState(false);
  const [showSensitivity, setShowSensitivity] = useState(false);

  // Build the recharts data array. Each row is one year, with the bar's Total
  // dollar value and (when cumulative/sensitivity are toggled on) the band
  // envelopes.
  const data = useMemo(() => {
    if (years.length === 0) return [];
    const fuelLowMul = 0.8;
    const fuelHighMul = 1.2;
    const depLowMul = 0.9;
    const depHighMul = 1.1;

    let cum = 0;
    let cumLow = 0;
    let cumHigh = 0;
    return years.map((y) => {
      const total = yearTotal(y);
      cum += total;
      const yLow = y.fuel * fuelLowMul + y.idleFuel + y.maintenance + y.insurance + y.registration + y.labor + y.depreciation * depLowMul;
      const yHigh = y.fuel * fuelHighMul + y.idleFuel + y.maintenance + y.insurance + y.registration + y.labor + y.depreciation * depHighMul;
      cumLow += yLow;
      cumHigh += yHigh;
      return {
        year: y.year,
        Total: Math.round(total),
        Cumulative: Math.round(cum),
        BandLow: Math.round(cumLow),
        BandHigh: Math.round(cumHigh),
        // Per-category values stashed so the custom tooltip can show the
        // mix without re-running the breakdown lookup.
        _fuel: Math.round(y.fuel),
        _idleFuel: Math.round(y.idleFuel),
        _maintenance: Math.round(y.maintenance),
        _insurance: Math.round(y.insurance),
        _registration: Math.round(y.registration),
        _labor: Math.round(y.labor),
        _depreciation: Math.round(y.depreciation),
      };
    });
  }, [years, totalFuel, totalDepreciation]);

  if (years.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-sm" style={{ color: 'var(--white-dim)' }}>
        No yearly cost data available.
      </div>
    );
  }

  const ChartImpl = showCumulative || showSensitivity ? ComposedChart : BarChart;

  // Sensitivity band-width as % of central final-year cumulative TCO. Used
  // to decide whether to show the "narrow band" callout below the chart.
  const lastRow = data[data.length - 1];
  const centralFinal = lastRow?.Cumulative ?? 0;
  const bandSpan = (lastRow?.BandHigh ?? 0) - (lastRow?.BandLow ?? 0);
  const bandWidthPct = centralFinal > 0 ? (bandSpan / centralFinal) * 100 : 0;
  const showNarrowBandNote = showCumulative && showSensitivity && bandWidthPct < LOW_SENSITIVITY_THRESHOLD_PCT;

  // Phase 25.3: pin the right-axis ceiling to the full band-high envelope
  // even when the sensitivity toggle is off. Otherwise recharts auto-scales
  // the right axis to whichever of {Cumulative, BandHigh} is actually
  // rendered, and the cumulative line visibly drops when the band appears —
  // the line data itself is unchanged, but the rendered Y coordinate
  // compresses because the axis maximum grew. Pinning makes the central
  // line position stable across toggle states.
  const rightAxisMax = Math.max(...data.map((d) => d.BandHigh), 1) * 1.05;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 8,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: 12, color: 'var(--white-dim)' }}>
          {fleetVehicleCount && fleetVehicleCount > 0
            ? `Fleet total — all ${fleetVehicleCount} vehicle${fleetVehicleCount === 1 ? '' : 's'}`
            : 'Annual cost walk'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--white-dim)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showCumulative}
              onChange={(e) => {
                const v = e.target.checked;
                setShowCumulative(v);
                if (!v) setShowSensitivity(false);
              }}
            />
            Show cumulative
          </label>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: 'var(--white-dim)',
              opacity: showCumulative ? 1 : 0.4,
              cursor: showCumulative ? 'pointer' : 'not-allowed',
            }}
            title={showCumulative ? '' : 'Enable cumulative line first'}
          >
            <input
              type="checkbox"
              disabled={!showCumulative}
              checked={showSensitivity}
              onChange={(e) => setShowSensitivity(e.target.checked)}
            />
            {SENSITIVITY_LABEL}
          </label>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={420}>
        <ChartImpl data={data} margin={{ top: 16, right: 32, left: 16, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e2530" />
          <XAxis
            dataKey="year"
            stroke="#8a93a2"
            tick={{ fill: '#8a93a2', fontSize: 14 }}
            label={{ value: 'Year', position: 'insideBottom', offset: -2, fill: '#8a93a2', fontSize: 14 }}
          />
          <YAxis
            yAxisId="left"
            stroke="#8a93a2"
            tick={{ fill: '#8a93a2', fontSize: 14 }}
            tickFormatter={fmtDollar}
            width={80}
          />
          {showCumulative && (
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="#8a93a2"
              tick={{ fill: '#8a93a2', fontSize: 14 }}
              tickFormatter={fmtDollar}
              width={90}
              domain={[0, rightAxisMax]}
            />
          )}
          <Tooltip
            content={<SingleTooltipContent />}
            cursor={{ fill: 'rgba(148,163,184,0.06)' }}
          />

          {showCumulative && showSensitivity && (
            <Area
              yAxisId="right"
              type="monotone"
              dataKey={(d: { BandLow: number; BandHigh: number }) => [d.BandLow, d.BandHigh]}
              name="Sensitivity envelope"
              stroke="none"
              fill={BAND_COLOR}
              fillOpacity={0.18}
              isAnimationActive={false}
              legendType="none"
              activeDot={false}
            />
          )}

          <Bar
            yAxisId="left"
            dataKey="Total"
            fill={SINGLE_BAR_COLOR}
            isAnimationActive={false}
            radius={[2, 2, 0, 0]}
          />

          {showCumulative && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="Cumulative"
              name="Cumulative TCO"
              stroke={CUMULATIVE_COLOR}
              strokeWidth={3}
              dot={{ r: 3, fill: CUMULATIVE_COLOR }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          )}
        </ChartImpl>
      </ResponsiveContainer>

      {showNarrowBandNote && (
        <div
          data-testid="sensitivity-narrow-band-note"
          style={{
            marginTop: 8,
            fontSize: 11,
            color: 'var(--tco-ink-3)',
            fontStyle: 'italic',
            lineHeight: 1.45,
          }}
        >
          Sensitivity band is narrow ({bandWidthPct.toFixed(1)}% of total TCO) — this vehicle's cost is
          not highly sensitive to fuel price or residual value changes over this horizon.
        </div>
      )}

      <CategoryDetailDisclosure testId="single-category-detail">
        <CategoryTableBody years={years} />
      </CategoryDetailDisclosure>
    </div>
  );
}

// ── Custom tooltip — single scenario ──────────────────────────────────────────

interface TooltipPayloadDatum {
  year: number;
  Total: number;
  Cumulative: number;
  BandLow: number;
  BandHigh: number;
  _fuel: number;
  _idleFuel: number;
  _maintenance: number;
  _insurance: number;
  _registration: number;
  _labor: number;
  _depreciation: number;
}

function SingleTooltipContent(props: {
  active?: boolean;
  payload?: Array<{ payload: TooltipPayloadDatum }>;
  label?: number;
}) {
  if (!props.active || !props.payload?.length) return null;
  const d = props.payload[0].payload;
  const rows: Array<{ label: string; v: number }> = [
    { label: 'Depreciation',  v: d._depreciation },
    { label: 'Fuel',          v: d._fuel },
    { label: 'Idle Fuel',     v: d._idleFuel },
    { label: 'Maintenance',   v: d._maintenance },
    { label: 'Insurance',     v: d._insurance },
    { label: 'Registration',  v: d._registration },
    { label: 'Labor',         v: d._labor },
  ].filter((r) => r.v > 0);
  return (
    <div style={tooltipBoxStyle}>
      <div style={tooltipHeaderStyle}>Year {props.label}</div>
      <div style={{ ...tooltipRowStyle, fontWeight: 600, marginBottom: 4 }}>
        <span>Total</span>
        <span style={{ marginLeft: 16 }}>{fmtDollar(d.Total)}</span>
      </div>
      {rows.map((r) => (
        <div key={r.label} style={tooltipRowStyle}>
          <span>{r.label}</span>
          <span style={{ marginLeft: 16 }}>{fmtDollar(r.v)}</span>
        </div>
      ))}
    </div>
  );
}

const tooltipBoxStyle: React.CSSProperties = {
  background: '#111418',
  border: '1px solid #1e2530',
  borderRadius: 8,
  padding: '10px 12px',
  color: '#e5e7eb',
  fontSize: 12,
  minWidth: 200,
};
const tooltipHeaderStyle: React.CSSProperties = {
  color: '#8a93a2',
  fontSize: 12,
  marginBottom: 6,
};
const tooltipRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  lineHeight: 1.5,
};

// ── Grouped (multi-series) mode ───────────────────────────────────────────────
//
// Phase 24.3.2: the compare views previously rendered two independent
// YearByCostChart instances side by side, each with its own Y axis. That made
// visual comparison misleading because the two scales drifted. The grouped
// component renders two bars per year (one per series) on a single shared
// Y axis. Phase 25.2 collapsed the per-category stack into a single total
// bar per series so the year-on-year story stays legible — the category
// detail moved into the table below the chart.

export interface SeriesInput {
  name: string;
  color: string;            // scenario-level accent (legend chip + cumulative line)
  years: YearBreakdown[];
  totalFuel: number;        // baseline lifetime fuel (used for sensitivity band)
  totalDepreciation: number;
}

interface GroupedProps {
  seriesA: SeriesInput;
  seriesB: SeriesInput;
  headerSubtitle?: string;
}

function buildSeriesYearMap(s: SeriesInput): Map<number, YearBreakdown> {
  const m = new Map<number, YearBreakdown>();
  for (const y of s.years) m.set(y.year, y);
  return m;
}

export function GroupedYearByCostChart({ seriesA, seriesB, headerSubtitle }: GroupedProps) {
  const [showCumulative, setShowCumulative] = useState(false);
  const [showSensitivity, setShowSensitivity] = useState(false);

  const data = useMemo(() => {
    const aMap = buildSeriesYearMap(seriesA);
    const bMap = buildSeriesYearMap(seriesB);
    const allYears = Array.from(new Set([...aMap.keys(), ...bMap.keys()])).sort((x, y) => x - y);
    if (allYears.length === 0) return [];

    const fuelLowMul = 0.8;
    const fuelHighMul = 1.2;
    const depLowMul = 0.9;
    const depHighMul = 1.1;

    let aCum = 0, bCum = 0;
    let aCumLow = 0, aCumHigh = 0;
    let bCumLow = 0, bCumHigh = 0;

    return allYears.map((yr) => {
      const a = aMap.get(yr);
      const b = bMap.get(yr);
      const aTotal = a ? yearTotal(a) : 0;
      const bTotal = b ? yearTotal(b) : 0;
      aCum += aTotal;
      bCum += bTotal;
      if (a) {
        aCumLow += a.fuel * fuelLowMul + a.idleFuel + a.maintenance + a.insurance + a.registration + a.labor + a.depreciation * depLowMul;
        aCumHigh += a.fuel * fuelHighMul + a.idleFuel + a.maintenance + a.insurance + a.registration + a.labor + a.depreciation * depHighMul;
      }
      if (b) {
        bCumLow += b.fuel * fuelLowMul + b.idleFuel + b.maintenance + b.insurance + b.registration + b.labor + b.depreciation * depLowMul;
        bCumHigh += b.fuel * fuelHighMul + b.idleFuel + b.maintenance + b.insurance + b.registration + b.labor + b.depreciation * depHighMul;
      }
      return {
        year: yr,
        a_Total: Math.round(aTotal),
        b_Total: Math.round(bTotal),
        a_Cumulative: Math.round(aCum),
        b_Cumulative: Math.round(bCum),
        a_BandLow: Math.round(aCumLow),
        a_BandHigh: Math.round(aCumHigh),
        b_BandLow: Math.round(bCumLow),
        b_BandHigh: Math.round(bCumHigh),
      };
    });
  }, [seriesA, seriesB]);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-sm" style={{ color: 'var(--white-dim)' }}>
        No yearly cost data available.
      </div>
    );
  }

  const ChartImpl = showCumulative || showSensitivity ? ComposedChart : BarChart;

  // Narrow-band note uses the *wider* of A's and B's final-year band as the
  // judgment input — if either scenario is sensitive, the band is not narrow.
  const last = data[data.length - 1];
  const aCentral = last?.a_Cumulative ?? 0;
  const bCentral = last?.b_Cumulative ?? 0;
  const aSpan = (last?.a_BandHigh ?? 0) - (last?.a_BandLow ?? 0);
  const bSpan = (last?.b_BandHigh ?? 0) - (last?.b_BandLow ?? 0);
  const aPct = aCentral > 0 ? (aSpan / aCentral) * 100 : 0;
  const bPct = bCentral > 0 ? (bSpan / bCentral) * 100 : 0;
  const maxBandPct = Math.max(aPct, bPct);
  const showNarrowBandNote = showCumulative && showSensitivity && maxBandPct < LOW_SENSITIVITY_THRESHOLD_PCT;

  // Phase 25.3: pin the right-axis ceiling to the wider of the two final-
  // year band-high envelopes so the central cumulative lines do not visibly
  // drop when the sensitivity toggle is switched on (see single-scenario
  // version above for rationale).
  const rightAxisMax = Math.max(
    ...data.map((d) => Math.max(d.a_BandHigh, d.b_BandHigh)),
    1,
  ) * 1.05;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 8,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: 'var(--white-dim)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 14, height: 10, background: seriesA.color, borderRadius: 2 }} />
              {seriesA.name}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                width: 14, height: 10, background: seriesB.color, borderRadius: 2,
              }} />
              {seriesB.name}
            </span>
          </div>
          {headerSubtitle && (
            <div style={{ fontSize: 11, color: 'var(--white-dim)', opacity: 0.85 }}>
              {headerSubtitle}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--white-dim)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showCumulative}
              onChange={(e) => {
                const v = e.target.checked;
                setShowCumulative(v);
                if (!v) setShowSensitivity(false);
              }}
            />
            Show cumulative
          </label>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: 'var(--white-dim)',
              opacity: showCumulative ? 1 : 0.4,
              cursor: showCumulative ? 'pointer' : 'not-allowed',
            }}
            title={showCumulative ? '' : 'Enable cumulative line first'}
          >
            <input
              type="checkbox"
              disabled={!showCumulative}
              checked={showSensitivity}
              onChange={(e) => setShowSensitivity(e.target.checked)}
            />
            {SENSITIVITY_LABEL}
          </label>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={420}>
        <ChartImpl data={data} margin={{ top: 16, right: 32, left: 16, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e2530" />
          <XAxis
            dataKey="year"
            stroke="#8a93a2"
            tick={{ fill: '#8a93a2', fontSize: 14 }}
            label={{ value: 'Year', position: 'insideBottom', offset: -2, fill: '#8a93a2', fontSize: 14 }}
          />
          <YAxis
            yAxisId="left"
            stroke="#8a93a2"
            tick={{ fill: '#8a93a2', fontSize: 14 }}
            tickFormatter={fmtDollar}
            width={80}
          />
          {showCumulative && (
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="#8a93a2"
              tick={{ fill: '#8a93a2', fontSize: 14 }}
              tickFormatter={fmtDollar}
              width={90}
              domain={[0, rightAxisMax]}
            />
          )}
          <Tooltip
            content={
              <GroupedTooltipContent aName={seriesA.name} bName={seriesB.name} />
            }
            cursor={{ fill: 'rgba(148,163,184,0.06)' }}
          />

          {showCumulative && showSensitivity && (
            <>
              <Area
                yAxisId="right"
                type="monotone"
                dataKey={(d: { a_BandLow: number; a_BandHigh: number }) => [d.a_BandLow, d.a_BandHigh]}
                name={`${seriesA.name} sensitivity`}
                stroke="none"
                fill={seriesA.color}
                fillOpacity={0.14}
                isAnimationActive={false}
                legendType="none"
                activeDot={false}
              />
              <Area
                yAxisId="right"
                type="monotone"
                dataKey={(d: { b_BandLow: number; b_BandHigh: number }) => [d.b_BandLow, d.b_BandHigh]}
                name={`${seriesB.name} sensitivity`}
                stroke="none"
                fill={seriesB.color}
                fillOpacity={0.14}
                isAnimationActive={false}
                legendType="none"
                activeDot={false}
              />
            </>
          )}

          <Bar
            yAxisId="left"
            dataKey="a_Total"
            fill={seriesA.color}
            isAnimationActive={false}
            radius={[2, 2, 0, 0]}
            name={seriesA.name}
          />
          <Bar
            yAxisId="left"
            dataKey="b_Total"
            fill={seriesB.color}
            isAnimationActive={false}
            radius={[2, 2, 0, 0]}
            name={seriesB.name}
          />

          {showCumulative && (
            <>
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="a_Cumulative"
                name={`${seriesA.name} — cumulative`}
                stroke={seriesA.color}
                strokeWidth={3}
                dot={{ r: 3, fill: seriesA.color }}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="b_Cumulative"
                name={`${seriesB.name} — cumulative`}
                stroke={seriesB.color}
                strokeWidth={3}
                strokeDasharray="5 4"
                dot={{ r: 3, fill: seriesB.color }}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            </>
          )}
        </ChartImpl>
      </ResponsiveContainer>

      {showNarrowBandNote && (
        <div
          data-testid="sensitivity-narrow-band-note"
          style={{
            marginTop: 8,
            fontSize: 11,
            color: 'var(--tco-ink-3)',
            fontStyle: 'italic',
            lineHeight: 1.45,
          }}
        >
          Sensitivity band is narrow (max {maxBandPct.toFixed(1)}% of total TCO) — this comparison's
          outcome is not highly sensitive to fuel price or residual value changes over this horizon.
        </div>
      )}

      <CategoryDetailDisclosure testId="grouped-category-detail">
        <GroupedCategoryTables seriesA={seriesA} seriesB={seriesB} />
      </CategoryDetailDisclosure>
    </div>
  );
}

function GroupedCategoryTables({
  seriesA,
  seriesB,
}: {
  seriesA: SeriesInput;
  seriesB: SeriesInput;
}) {
  // Phase 25.3: previously rendered side-by-side via
  // `repeat(auto-fit, minmax(420px, 1fr))`, which at the typical compare-
  // page width (≈960–1100px) crammed the two sub-tables into two columns
  // with too little horizontal room — year cells then overlapped category
  // labels. Stack vertically (Scenario A above, Scenario B below) so each
  // table gets the full row width; the scenario name + color chip header
  // makes the grouping obvious.
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--tco-ink-2)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontWeight: 600,
            marginBottom: 4,
          }}
        >
          <span
            aria-hidden
            style={{
              display: 'inline-block',
              width: 10,
              height: 10,
              borderRadius: 2,
              background: seriesA.color,
              marginRight: 6,
              verticalAlign: 'middle',
            }}
          />
          {seriesA.name}
        </div>
        <CategoryTableBody years={seriesA.years} />
      </div>
      <div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--tco-ink-2)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontWeight: 600,
            marginBottom: 4,
          }}
        >
          <span
            aria-hidden
            style={{
              display: 'inline-block',
              width: 10,
              height: 10,
              borderRadius: 2,
              background: seriesB.color,
              marginRight: 6,
              verticalAlign: 'middle',
            }}
          />
          {seriesB.name}
        </div>
        <CategoryTableBody years={seriesB.years} />
      </div>
    </div>
  );
}

// ── Custom tooltip — grouped chart ────────────────────────────────────────────

interface GroupedTooltipPayload {
  year: number;
  a_Total: number;
  b_Total: number;
}

function GroupedTooltipContent(props: {
  active?: boolean;
  payload?: Array<{ payload: GroupedTooltipPayload }>;
  label?: number;
  aName: string;
  bName: string;
}) {
  if (!props.active || !props.payload?.length) return null;
  const d = props.payload[0].payload;
  const delta = d.b_Total - d.a_Total;
  const lowerLabel = Math.abs(delta) < 1
    ? 'tied'
    : delta < 0
      ? `${props.bName} lower by ${fmtDollar(Math.abs(delta))}`
      : `${props.aName} lower by ${fmtDollar(Math.abs(delta))}`;
  return (
    <div style={tooltipBoxStyle}>
      <div style={tooltipHeaderStyle}>Year {props.label}</div>
      <div style={tooltipRowStyle}>
        <span>{props.aName}</span>
        <span style={{ marginLeft: 16 }}>{fmtDollar(d.a_Total)}</span>
      </div>
      <div style={tooltipRowStyle}>
        <span>{props.bName}</span>
        <span style={{ marginLeft: 16 }}>{fmtDollar(d.b_Total)}</span>
      </div>
      <div style={{ ...tooltipRowStyle, marginTop: 4, color: '#8a93a2' }}>
        <span>Delta</span>
        <span style={{ marginLeft: 16 }}>{lowerLabel}</span>
      </div>
    </div>
  );
}

// ── Derivation helpers ────────────────────────────────────────────────────────
//
// Personal mode: VehicleResult exposes horizon-total operating subtotals and
// a monthly_value_path. Yearly depreciation is the residual delta across
// each 12-month window (year 1 starts at purchase price, fallback to month-0
// residual if purchasePrice is unknown). Operating subtotals are spread flat
// across the horizon — a conservative assumption documented in the prompt.
// Tires and repairs are pooled into Maintenance to keep the legend tight.

export function deriveYearsFromVehicleResult(
  vehicle: VehicleResult,
  horizonMonths: number,
  purchasePrice: number,
): YearBreakdown[] {
  const years = Math.max(0, Math.floor(horizonMonths / 12));
  if (years === 0) return [];

  // Phase 26.5: prefer the backend year-by-year breakdown when present
  // (maintenance aging + fuel inflation + insurance step-up already applied).
  // Falls back to the legacy flat-spread derivation when the field is missing
  // (older response shape from a cached compute result).
  const backend = vehicle.yearly_costs;
  if (Array.isArray(backend) && backend.length > 0) {
    return backend.map((y) => ({
      year: y.year,
      fuel: y.fuel,
      idleFuel: y.idle_fuel,
      maintenance: y.maintenance,
      insurance: y.insurance,
      registration: y.registration,
      labor: y.labor,
      depreciation: y.depreciation,
    }));
  }

  const op = vehicle.operating as Record<string, unknown> | null;
  const bd = op?.['operating_breakdown'] as Record<string, unknown> | undefined;
  const idleTotal = Number(op?.['idle_fuel_cost_total'] ?? 0);
  const fuelTotal = Number(bd?.['fuel_cost_total'] ?? 0);
  const insTotal = Number(bd?.['insurance_total'] ?? 0);
  const regTotal = Number(bd?.['registration_total'] ?? 0);
  const maintTotal = Number(bd?.['maintenance_total'] ?? 0);
  const tireTotal = Number(bd?.['tire_cost_total'] ?? 0);
  const repairTotal = Number(bd?.['repair_cost_total'] ?? 0);

  // Maintenance bucket consolidates maintenance + tires + repairs because the
  // operating ledger separates them but the cost walk's purpose is a clean
  // year-on-year story; three near-identical green segments would be noisy.
  const annualFuel = fuelTotal / years;
  const annualIdle = idleTotal / years;
  const annualMaint = (maintTotal + tireTotal + repairTotal) / years;
  const annualIns = insTotal / years;
  const annualReg = regTotal / years;

  const dep = vehicle.depreciation as Record<string, unknown>;
  const path = Array.isArray(dep['monthly_value_path']) ? (dep['monthly_value_path'] as number[]) : [];

  const monthlyValueAt = (m: number): number | null => {
    if (m < 0 || m >= path.length) return null;
    const v = path[m];
    return typeof v === 'number' ? v : null;
  };

  return Array.from({ length: years }, (_, i) => {
    const year = i + 1;
    const startMonth = i * 12;
    const endMonth = (i + 1) * 12;
    const startVal = i === 0 && purchasePrice > 0
      ? purchasePrice
      : (monthlyValueAt(startMonth) ?? purchasePrice);
    const endVal = monthlyValueAt(Math.min(endMonth, path.length - 1)) ?? startVal;
    const depYear = Math.max(0, startVal - endVal);
    return {
      year,
      fuel: annualFuel,
      idleFuel: annualIdle,
      maintenance: annualMaint,
      insurance: annualIns,
      registration: annualReg,
      labor: 0,
      depreciation: depYear,
    };
  });
}

// Fleet mode: FleetTCOResponse exposes per-year operating subtotals as flat
// annual figures (used directly here) plus a per-line-item monthly_value_path
// inside per_vehicle_tco.depreciation (Phase 24.3.2). Yearly depreciation is
// derived from the line-item monthly paths aggregated by quantity, then
// sampled at year boundaries. This gives a strictly decreasing exponential
// shape consistent with the depreciation engine, rather than the bucketed
// Argonne-curve lookup that powered cumulative_cost_by_year.residual_at_year_end
// in earlier phases (which produced flat year 2-3 and 4-5 segments).

export function deriveYearsFromFleetResponse(result: FleetTCOResponse): YearBreakdown[] {
  const t = result.fleet_totals;
  const years = Math.max(0, Math.floor(t.ownership_period_years));
  if (years === 0) return [];

  // Phase 26.5: prefer the backend fleet-aggregated year-by-year breakdown
  // when present. Falls back to the legacy path/flat-spread derivation
  // when fleet_totals.yearly_costs is missing.
  const backend = t.yearly_costs;
  if (Array.isArray(backend) && backend.length > 0) {
    return backend.map((y) => ({
      year: y.year,
      fuel: y.fuel,
      idleFuel: y.idle_fuel,
      maintenance: y.maintenance,
      insurance: y.insurance,
      registration: y.registration,
      labor: y.labor,
      depreciation: y.depreciation,
    }));
  }

  // Aggregate monthly_value_path across line items, weighted by quantity. Each
  // line item's path is in dollars-per-vehicle; quantity scales to fleet
  // residual at each month. Paths may have different lengths if line items
  // run different horizons — clamp to the shortest.
  const paths: { path: number[]; quantity: number }[] = [];
  for (const li of result.line_items) {
    const pv = li.per_vehicle_tco as Record<string, unknown> | undefined;
    const dep = pv?.['depreciation'] as Record<string, unknown> | undefined;
    const rawPath = dep?.['monthly_value_path'];
    if (Array.isArray(rawPath) && rawPath.length > 0) {
      paths.push({
        path: rawPath.filter((v) => typeof v === 'number') as number[],
        quantity: Number(li.quantity) || 0,
      });
    }
  }

  const aggregatePathLength = paths.length > 0
    ? Math.min(...paths.map((p) => p.path.length))
    : 0;

  const aggregatePath: number[] = [];
  for (let m = 0; m < aggregatePathLength; m++) {
    let sum = 0;
    for (const { path, quantity } of paths) {
      sum += path[m] * quantity;
    }
    aggregatePath.push(sum);
  }

  return Array.from({ length: years }, (_, i) => {
    const year = i + 1;
    const startMonth = i * 12;
    const endMonth = (i + 1) * 12;
    let depYear = 0;
    if (aggregatePath.length > 0) {
      const startVal = aggregatePath[Math.min(startMonth, aggregatePath.length - 1)];
      const endVal = aggregatePath[Math.min(endMonth, aggregatePath.length - 1)];
      depYear = Math.max(0, startVal - endVal);
    } else {
      // Fallback: no monthly_value_path available (older response shape).
      // Reuse the Argonne-bucketed cumulative_cost_by_year so the bars still
      // render — accept the flat shape as a degraded state.
      const cumByYear = t.cumulative_cost_by_year ?? [];
      const curr = cumByYear[i];
      const prev = i === 0 ? null : cumByYear[i - 1];
      if (curr) {
        if (i === 0) {
          depYear = Math.max(0, t.total_acquisition_cost - curr.residual_at_year_end);
        } else if (prev) {
          depYear = Math.max(0, prev.residual_at_year_end - curr.residual_at_year_end);
        }
      }
    }
    return {
      year,
      fuel: t.total_annual_fuel_cost,
      idleFuel: t.total_annual_idle_fuel_cost,
      maintenance: t.total_annual_maintenance_cost,
      insurance: t.total_annual_insurance_cost,
      registration: t.total_annual_registration_cost,
      labor: t.total_annual_labor_cost,
      depreciation: depYear,
    };
  });
}
